import { useControlledState } from '@react-stately/utils';
import * as React from 'react';

import { useDeferredFlush, useLiveRef } from '@gitbook/util-react-hooks';

interface UseCoverResizerOptions {
    /** A ref to the resize handle element */
    resizeHandleRef: React.RefObject<HTMLButtonElement | null>;
    /** The current height of the cover in pixels (controlled) */
    value?: number;
    /** The default height of the cover in pixels (uncontrolled) */
    defaultValue?: number;
    /** The minimum height of the cover in pixels */
    min: number;
    /** The maximum height of the cover in pixels */
    max: number;
    /** Whether the cover is read only */
    readOnly?: boolean;
    /** A callback to commit the final height of the cover */
    onChange?: (height: number) => void;
}

interface UseCoverResizerResult {
    /** The selected height of the cover in pixels */
    height: number;
    /** Whether the cover is being resized */
    resizing: boolean;
}

/**
 * Hook to handle the resizing of the cover, returning:
 * - `resizing`: whether the cover is being resized
 * - `height`: the height of the cover in pixels
 */
export function useCoverResizer(options: UseCoverResizerOptions): UseCoverResizerResult {
    const { min, max, readOnly, onChange, resizeHandleRef } = options;
    const [committedHeight, setCommittedHeight] = useControlledState(
        options.value,
        options.defaultValue ?? 0,
        onChange
    );
    const [liveHeight, setLiveHeight] = React.useState(committedHeight);
    const [resizing, setResizing] = React.useState(false);
    const dragOriginHeightRef = React.useRef(committedHeight);
    const dragStartYRef = React.useRef<number | null>(null);
    const activePointerIdRef = React.useRef<number | null>(null);
    const liveHeightRef = useLiveRef(liveHeight);
    const endKeyboardResize = useDeferredFlush<void>(1000);
    const commitKeyboardHeight = useDeferredFlush<void>(1000);

    React.useEffect(() => {
        if (!resizing && liveHeight !== committedHeight) {
            setLiveHeight(committedHeight);
            dragOriginHeightRef.current = committedHeight;
        }
    }, [committedHeight, liveHeight, resizing]);

    const clampHeight = React.useCallback(
        (value: number) => Math.min(max, Math.max(min, value)),
        [min, max]
    );

    // Effect for pointer down on resize handle
    React.useEffect(() => {
        const handle = resizeHandleRef.current;
        if (!handle || readOnly) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            if (event.button !== 0) {
                return;
            }
            dragOriginHeightRef.current = liveHeightRef.current;
            dragStartYRef.current = event.clientY;
            activePointerIdRef.current = event.pointerId;
            if (handle.setPointerCapture) {
                handle.setPointerCapture(event.pointerId);
            }
            setResizing(true);
        };

        handle.addEventListener('pointerdown', handlePointerDown);
        return () => {
            handle.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [resizeHandleRef, readOnly, liveHeightRef]);

    // Effect for pointer move and pointer up during resize
    React.useEffect(() => {
        if (!resizing) {
            return;
        }

        const handlePointerMove = (event: PointerEvent) => {
            if (dragStartYRef.current === null || activePointerIdRef.current !== event.pointerId) {
                return;
            }

            const deltaY = event.clientY - dragStartYRef.current;
            const computedHeight = dragOriginHeightRef.current + deltaY;
            const clamped = clampHeight(computedHeight);

            const step = event.shiftKey ? 10 : 1;
            const rounded = Math.round(clamped / step) * step;

            if (rounded !== liveHeightRef.current) {
                setLiveHeight(rounded);
            }
        };

        const finalizePointer = (event: PointerEvent) => {
            if (dragStartYRef.current === null || activePointerIdRef.current !== event.pointerId) {
                return;
            }

            dragStartYRef.current = null;
            const handle = resizeHandleRef.current;
            if (handle?.hasPointerCapture?.(event.pointerId)) {
                handle.releasePointerCapture(event.pointerId);
            }
            activePointerIdRef.current = null;
            setResizing(false);

            const finalHeight = liveHeightRef.current;
            dragOriginHeightRef.current = finalHeight;
            setCommittedHeight(finalHeight);
            handle?.blur();
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', finalizePointer);
        window.addEventListener('pointercancel', finalizePointer);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', finalizePointer);
            window.removeEventListener('pointercancel', finalizePointer);
        };
    }, [clampHeight, liveHeightRef, resizeHandleRef, resizing, setCommittedHeight]);

    // Effect for keydown on resize handle
    React.useEffect(() => {
        const handle = resizeHandleRef.current;
        if (!handle || readOnly) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            const allowedKeys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
            if (!allowedKeys.includes(event.key)) {
                return;
            }

            // Cancel any scheduled resets
            endKeyboardResize.cancel();
            commitKeyboardHeight.cancel();

            // Set resizing to true to display the correct tooltip values and crosshair UI etc
            setResizing(true);

            const step = event.shiftKey ? 10 : 1; // Shift for larger steps
            let newHeight = liveHeightRef.current;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    newHeight = newHeight - step;
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    newHeight = newHeight + step;
                    break;
                case 'Home':
                    event.preventDefault();
                    newHeight = min;
                    break;
                case 'End':
                    event.preventDefault();
                    newHeight = max;
                    break;
                default:
                    return;
            }

            const clamped = clampHeight(newHeight);
            const rounded = Math.round(clamped);

            if (rounded !== liveHeightRef.current) {
                // Update live height immediately for visual feedback
                setLiveHeight(rounded);

                // Schedule committing the height after keyboard interaction settles
                // Use liveHeightRef to ensure we commit the latest value when the debounce fires
                commitKeyboardHeight(() => {
                    setCommittedHeight(liveHeightRef.current);
                });
            }

            // Schedule resetting the resizing state after keyboard interaction settles
            endKeyboardResize(() => {
                setResizing(false);
            });
        };

        handle.addEventListener('keydown', handleKeyDown);
        return () => {
            handle.removeEventListener('keydown', handleKeyDown);
            endKeyboardResize.flush();
            commitKeyboardHeight.flush();
        };
    }, [
        clampHeight,
        liveHeightRef,
        max,
        min,
        resizeHandleRef,
        readOnly,
        setCommittedHeight,
        endKeyboardResize,
        commitKeyboardHeight,
    ]);

    return {
        resizing,
        height: liveHeight,
    };
}
